import {
  ExtraTimePayEditFormValues,
  ValidateExtraTimePayEditFormArgs,
} from "src/types/extraTimePay";
import { isEqual } from "lodash";

import { getNumberOfDigitsAfterTheDecimal } from "./number";
import { ExtraTimePaySettings } from "../hooks/useLoadEtpSettings";
import { isDefined } from "@clipboard-health/util-ts";
import {
  SaveExtraTimePaySettingsArguments,
  ExtraTimePaySettingsDifferences,
} from "../hooks/useSaveEtpSettings";

export function validateExtraTimePayEditForm(
  data: ValidateExtraTimePayEditFormArgs
): string | undefined {
  const { path, formValues } = data;
  const [option, field] = path.split(".");
  /**
   * Skip validations in the following scenarios
   */
  if (
    (option === "firstWorkdayOption" && !formValues.isFirstWorkdayEnabled) ||
    (option === "secondWorkdayOption" && !formValues.isSecondWorkdayEnabled) ||
    (option === "weeklyOption" && !formValues.isWeeklyEnabled)
  ) {
    return undefined;
  }

  const value = formValues[option][field];
  if (field === "multiplier") {
    if (Number.isNaN(value) || value <= 1 || value > 3) {
      return "Select a rate multiplier between 1.05 and 3.";
    }

    if (getNumberOfDigitsAfterTheDecimal(value) > 2 || Math.round(value * 100) % 5 !== 0) {
      return "Multiplier must be an increment of 0.05.";
    }

    if (option === "secondWorkdayOption" && value <= formValues.firstWorkdayOption.multiplier) {
      return "Select a rate multiplier greater than the first Workday option or unselect.";
    }

    return undefined;
  }

  if (field === "threshold") {
    if (Number.isNaN(value) || value < 2 || value > 16) {
      return "Select an hour threshold between 2 and 16.";
    }

    if (value.toString().indexOf(".") >= 0) {
      return "Threshold must be a whole number.";
    }

    if (option === "secondWorkdayOption" && value <= formValues.firstWorkdayOption.threshold) {
      return "Select an hour threshold greater than the first Workday option or unselect.";
    }
  }
}

export function getExtraTimePayEditFormDefaultValues(
  extraTimePaySettings?: Pick<ExtraTimePaySettings, "workdayOptions" | "weeklyOption">
): ExtraTimePayEditFormValues {
  return {
    isFirstWorkdayEnabled: Boolean(extraTimePaySettings?.workdayOptions?.[0]),
    isSecondWorkdayEnabled: Boolean(extraTimePaySettings?.workdayOptions?.[1]),
    isWeeklyEnabled: Boolean(extraTimePaySettings?.weeklyOption),
    firstWorkdayOption: extraTimePaySettings?.workdayOptions?.[0] ?? {
      threshold: 8,
      multiplier: 1.5,
    },
    secondWorkdayOption: extraTimePaySettings?.workdayOptions?.[1] ?? {
      threshold: 12,
      multiplier: 2,
    },
    weeklyOption: extraTimePaySettings?.weeklyOption ?? {
      threshold: 40,
      multiplier: 1.5,
    },
  };
}

export function getExtraTimePaySettingsDifferences(
  args: SaveExtraTimePaySettingsArguments
): ExtraTimePaySettingsDifferences {
  const { updatedSettings, previousSettings } = args;

  const etpSettingsDifferences: ExtraTimePaySettingsDifferences = {};

  if (!isEqual(previousSettings.workdayOptions, updatedSettings.workdayOptions)) {
    etpSettingsDifferences.workdayOptions = updatedSettings.workdayOptions;
  }

  if (!isEqual(previousSettings.weeklyOption, updatedSettings.weeklyOption)) {
    etpSettingsDifferences.weeklyOption = updatedSettings.weeklyOption;
  }

  if (
    isDefined(updatedSettings.isEditableByFacility) &&
    !isEqual(previousSettings.isEditableByFacility, updatedSettings.isEditableByFacility)
  ) {
    etpSettingsDifferences.isEditableByFacility = updatedSettings.isEditableByFacility;
  }

  return etpSettingsDifferences;
}
